import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '../../shared/services/loader.service';
import { ApiService } from '../../shared/services/api.service';
import { NotificationService } from '../../shared/services/notification.service';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BreadcrumComponent } from '../breadcrum/breadcrum.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface BlogItem {
  id: number;
  title: string;
  description: string;
  author_name: string;
  author_description: string;
  created_date: string;
  status: string;
  images: string[];
  image_type: string[];
  video_url?: string;
  duration?: string;
  blog_id?: number;
  row_id?: number;
  _id?: number;
}

@Component({
  selector: 'app-blog-detail',
  standalone: true,
  imports: [CommonModule, BreadcrumComponent],
  templateUrl: './blog-detail.component.html',
  styleUrls: ['./blog-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogDetailComponent implements OnInit, OnDestroy {
  blogId: string | null = null;
  blogItem: BlogItem | null = null;
  recentBlogs: BlogItem[] = [];
  sub: Subscription = new Subscription();
  breadcrumbData: any[] = [];
  readTime: string = '5 mins read'; // Default read time, could be calculated based on content length
  isLoading: boolean = false;
  hasError: boolean = false;

  private destroy$ = new Subject<void>();
  private cancelPendingRequests$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loaderService: LoaderService,
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    this.initializeBreadcrumb();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      // Cancel any pending requests when navigating to a new blog
      this.cancelPendingRequests$.next();

      this.blogId = params['id'];
      if (this.blogId) {
        // Check if blog item was passed via router navigation state
        const stateBlog =
          typeof history !== 'undefined' && history.state?.blogItem
            ? history.state.blogItem
            : null;
        const stateId = (
          stateBlog?.id ??
          stateBlog?.blog_id ??
          stateBlog?.row_id ??
          stateBlog?._id
        )?.toString();

        if (stateBlog && stateId && stateId === this.blogId.toString()) {
          this.populateBlogDetail(stateBlog);
        } else {
          this.getBlogDetail();
        }
        this.getRecentBlogs();
      } else {
        this.isLoading = false;
        this.hasError = true;
        this.cdr.markForCheck();
      }
    });

    this.scrollToTop();
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions
    this.cancelPendingRequests$.next();
    this.cancelPendingRequests$.complete();
    this.destroy$.next();
    this.destroy$.complete();
    this.sub.unsubscribe();
  }

  scrollToTop(): void {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  initializeBreadcrumb(): void {
    this.breadcrumbData = [
      { label: 'Home', url: '/home' },
      { label: 'Blogs & Videos', url: '/blog-stories' },
      { label: 'Blog', active: true },
    ];
  }

  updateBreadcrumb(blogTitle?: string): void {
    this.breadcrumbData = [
      // { label: 'Home', url: '/home' },
      { label: 'Blogs & Videos', url: '/blog-stories' },
      { label: blogTitle || 'Blog', active: true },
    ];
  }

  onBreadcrumbClick(event: { label: string; url: any; index: number }): void {
    console.log('Breadcrumb clicked:', event);
  }

  populateBlogDetail(foundBlog: any): void {
    this.blogItem = foundBlog;
    this.hasError = false;
    this.isLoading = false;
    this.updateBreadcrumb(foundBlog.title);

    // Calculate read time based on description length (assuming 200 words per minute)
    if (foundBlog.description) {
      // Strip HTML tags for accurate word count
      const textContent = foundBlog.description
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const wordCount = textContent
        .split(/\s+/)
        .filter((word: string) => word.length > 0).length;
      const minutes = Math.max(1, Math.ceil(wordCount / 200)); // At least 1 minute
      this.readTime = `${minutes} mins read`;
    } else {
      this.readTime = '5 mins read'; // Default
    }

    this.cdr.markForCheck();
  }

  private matchesCurrentBlogId(item: any): boolean {
    if (!item) return false;
    const itemId = item.id ?? item.blog_id ?? item.row_id ?? item._id;
    return (
      itemId !== undefined &&
      itemId !== null &&
      itemId.toString() === this.blogId?.toString()
    );
  }

  getBlogDetail(): void {
    if (!this.blogId) {
      this.isLoading = false;
      this.hasError = true;
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.loaderService.show();

    const payload = {
      action_type: 'Get All Blogs',
      offset: 0,
      limit: 100, // Safe limit matching blog-stories-list
      search_text: '',
      sort_by: '',
      image_type: '', // Avoid filtering by screenType to ensure blog is returned
    };

    this.apiService
      .getBlogs(payload)
      .pipe(takeUntil(this.cancelPendingRequests$), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          let foundBlog: any = null;
          if (res?.success && Array.isArray(res.data)) {
            // Find the blog with matching ID safely across all possible id fields
            foundBlog = res.data.find((blog: any) =>
              this.matchesCurrentBlogId(blog),
            );
          } else if (
            res?.success &&
            res.data &&
            typeof res.data === 'object' &&
            this.matchesCurrentBlogId(res.data)
          ) {
            foundBlog = res.data;
          }

          if (foundBlog) {
            this.loaderService.hide();
            this.populateBlogDetail(foundBlog);
          } else {
            // Attempt fallback query with 'Get Blogs'
            this.fetchBlogDetailFallback();
          }
        },
        error: (err) => {
          console.warn('Get All Blogs failed, attempting fallback to Get Blogs', err);
          this.fetchBlogDetailFallback();
        },
      });
  }

  private fetchBlogDetailFallback(): void {
    const fallbackPayload = {
      action_type: 'Get Blogs',
      offset: 0,
      limit: 100,
      search_text: '',
      sort_by: '',
      image_type: '',
    };

    this.apiService
      .getBlogs(fallbackPayload)
      .pipe(takeUntil(this.cancelPendingRequests$), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.loaderService.hide();
          this.isLoading = false;
          let foundBlog: any = null;
          if (res?.success && Array.isArray(res.data)) {
            foundBlog = res.data.find((blog: any) =>
              this.matchesCurrentBlogId(blog),
            );
          } else if (
            res?.success &&
            res.data &&
            typeof res.data === 'object' &&
            this.matchesCurrentBlogId(res.data)
          ) {
            foundBlog = res.data;
          }

          if (foundBlog) {
            this.populateBlogDetail(foundBlog);
          } else {
            console.error('Blog not found with ID:', this.blogId);
            this.hasError = true;
            this.cdr.markForCheck();
          }
        },
        error: (err) => {
          console.error('Failed to fetch blog detail on fallback', err);
          this.loaderService.hide();
          this.isLoading = false;
          this.hasError = true;
          this.cdr.markForCheck();
        },
      });
  }

  getRecentBlogs(): void {
    const payload = {
      action_type: 'Get Blogs',
      offset: 0,
      limit: 10,
      search_text: '',
      sort_by: 'newest_to_oldest',
      image_type: '',
    };

    this.apiService
      .getBlogs(payload)
      .pipe(takeUntil(this.cancelPendingRequests$), takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.success && Array.isArray(res.data)) {
            // Filter out current blog if it exists
            this.recentBlogs = res.data
              .filter((blog: any) => {
                const itemId =
                  blog?.id ?? blog?.blog_id ?? blog?.row_id ?? blog?._id;
                return (
                  itemId !== undefined &&
                  itemId !== null &&
                  itemId.toString() !== this.blogId?.toString()
                );
              })
              .slice(0, 3);

            this.cdr.markForCheck();
          }
        },
        error: (err) => {
          console.error('Failed to fetch recent blogs', err);
        },
      });
  }

  navigateToBlog(blog: BlogItem): void {
    const id =
      blog?.id ??
      (blog as any)?.blog_id ??
      (blog as any)?.row_id ??
      (blog as any)?._id;
    if (id) {
      this.router.navigate(['/blog-detail', id], {
        state: { blogItem: blog },
      });
      this.scrollToTop();
    }
  }

  navigateToBlogStories(): void {
    this.router.navigate(['/blog-stories']);
  }

  hasImage(item: BlogItem | null): boolean {
    if (!item) return false;
    return (
      item.images &&
      Array.isArray(item.images) &&
      item.images.length > 0 &&
      !!item.images[0]
    );
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid date

      const day = date.getDate();
      const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();

      return `${day} ${month} ${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString || '';
    }
  }

  getSanitizedDescription(): SafeHtml {
    if (!this.blogItem?.description) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    // Use bypassSecurityTrustHtml to preserve HTML formatting
    return this.sanitizer.bypassSecurityTrustHtml(this.blogItem.description);
  }
}
